import * as React from 'react'
import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, label, error, hint, id, ...props }, ref) => {
    const inputId = id ?? (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined)

    const field = (
      <input
        type={type}
        id={inputId}
        className={cn(
          'flex h-10 w-full rounded border bg-card px-3.5 py-2 font-sans text-sm text-ink',
          'placeholder:text-muted-foreground/60',
          'transition-colors duration-150',
          'focus-visible:outline-none focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-green/20',
          'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-cream-2',
          error
            ? 'border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20 bg-[hsl(var(--overdue-bg))]'
            : 'border-input',
          className,
        )}
        ref={ref}
        aria-invalid={!!error}
        aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
        {...props}
      />
    )

    if (!label && !error && !hint) {
      return field
    }

    return (
      <div className="space-y-1.5">
        {label && <Label htmlFor={inputId}>{label}</Label>}
        {field}
        {hint && !error && (
          <p id={`${inputId}-hint`} className="font-sans text-xs text-muted-foreground">
            {hint}
          </p>
        )}
        {error && (
          <p id={`${inputId}-error`} className="font-sans text-xs text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>
    )
  },
)
Input.displayName = 'Input'

export { Input }
